import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, Mail, Phone } from "lucide-react";
import SectionReveal from "./SectionReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = [
  "Service / Wartung",
  "MFK-Vorbereitung",
  "Reparatur / Unfall",
  "Vespa kaufen",
  "Occasion anfragen",
  "Pony Mofa",
  "Zubehör",
  "Allgemein",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    topic: TOPICS[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte Name, E-Mail und Nachricht ausfüllen.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Grazie mille! Wir melden uns innert 24 Stunden.");
      setDone(true);
      setForm({ name: "", email: "", phone: "", topic: TOPICS[0], message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Etwas ist schiefgelaufen. Bitte rufen Sie uns direkt an.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      className="section relative bg-[var(--anthracite)] text-[var(--cream)] overflow-hidden"
      data-testid="contact-section"
    >
      <div className="tricolore absolute top-0 left-0 right-0 h-[3px]" />
      <div className="container-wide">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left */}
          <div className="lg:col-span-5">
            <SectionReveal>
              <span className="overline !text-[var(--sage-soft)]">Kontakt</span>
              <h2 className="h-display text-5xl md:text-6xl lg:text-7xl mt-6">
                Schreiben Sie <span className="italic-accent text-[var(--sage-soft)]">uns.</span>
              </h2>
              <p className="mt-8 text-[var(--cream)]/70 leading-relaxed max-w-md">
                Termin, Beratung, Probefahrt — wir freuen uns auf Ihre Nachricht. Antwort
                innerhalb 24 Stunden.
              </p>
            </SectionReveal>

            <SectionReveal delay={1} className="mt-12 space-y-6">
              <a
                href="tel:+41614218833"
                className="flex items-center gap-4 group"
                data-testid="contact-phone-link"
              >
                <span className="w-12 h-12 grid place-items-center border border-[var(--cream)]/30 group-hover:bg-[var(--sage-dark)] group-hover:border-[var(--sage-dark)] transition-colors">
                  <Phone size={16} strokeWidth={1.4} />
                </span>
                <div>
                  <p className="overline !text-[var(--sage-soft)] text-[10px]">Telefon</p>
                  <p className="font-serif text-xl mt-1">+41 61 421 88 33</p>
                </div>
              </a>
              <a
                href="mailto:original@vespa-basel.ch"
                className="flex items-center gap-4 group"
                data-testid="contact-email-link"
              >
                <span className="w-12 h-12 grid place-items-center border border-[var(--cream)]/30 group-hover:bg-[var(--sage-dark)] group-hover:border-[var(--sage-dark)] transition-colors">
                  <Mail size={16} strokeWidth={1.4} />
                </span>
                <div>
                  <p className="overline !text-[var(--sage-soft)] text-[10px]">E-Mail</p>
                  <p className="font-serif text-xl mt-1">original@vespa-basel.ch</p>
                </div>
              </a>
            </SectionReveal>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-7">
            <SectionReveal>
              <form
                onSubmit={submit}
                className="space-y-6"
                data-testid="contact-form"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <Field
                    label="Name"
                    name="name"
                    value={form.name}
                    onChange={update("name")}
                    required
                  />
                  <Field
                    label="E-Mail"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    required
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <Field
                    label="Telefon (optional)"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                  />
                  <div className="relative">
                    <label className="overline !text-[var(--sage-soft)] block mb-3">
                      Thema
                    </label>
                    <select
                      name="topic"
                      value={form.topic}
                      onChange={update("topic")}
                      className="w-full bg-transparent border-b border-[var(--cream)]/30 py-3 text-[var(--cream)] focus:outline-none focus:border-[var(--sage-soft)] transition-colors appearance-none cursor-pointer"
                      data-testid="contact-topic-select"
                    >
                      {TOPICS.map((t) => (
                        <option key={t} value={t} className="bg-[var(--anthracite)]">
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <Field
                  label="Ihre Nachricht"
                  name="message"
                  as="textarea"
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  required
                />
                <div className="pt-4 flex flex-wrap gap-4 items-center">
                  <motion.button
                    type="submit"
                    disabled={loading || done}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex items-center gap-3 px-8 py-4 bg-[var(--cream)] text-[var(--anthracite)] text-xs uppercase tracking-[0.22em] font-medium hover:bg-[var(--sage-soft)] transition-colors disabled:opacity-60"
                    data-testid="contact-submit"
                  >
                    {loading ? "Wird gesendet…" : done ? "Gesendet · Grazie" : "Nachricht senden"}
                    <Send size={14} strokeWidth={1.6} />
                  </motion.button>
                  <p className="text-[11px] text-[var(--cream)]/50 uppercase tracking-[0.18em]">
                    Wir antworten innert 24h
                  </p>
                </div>
              </form>
            </SectionReveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, as = "input", ...rest }) {
  const Tag = as;
  return (
    <div className="relative">
      <label
        htmlFor={`field-${name}`}
        className="overline !text-[var(--sage-soft)] block mb-3"
      >
        {label}
      </label>
      <Tag
        id={`field-${name}`}
        name={name}
        data-testid={`contact-${name}`}
        className="w-full bg-transparent border-b border-[var(--cream)]/30 py-3 text-[var(--cream)] focus:outline-none focus:border-[var(--sage-soft)] transition-colors placeholder:text-[var(--cream)]/30 resize-none"
        {...rest}
      />
    </div>
  );
}
